<template>
    <div class="flex flex-col items-start">
        <Card class="w-full max-w-2xl border" :class="frameClass" :pt="{ body: { class: 'p-3' } }">
            <template #content>
                <div class="flex items-start gap-2">
                    <i :class="iconClass" />
                    <div class="flex-1 min-w-0">
                        <div class="text-sm font-medium">{{ headline }}</div>

                        <!-- Extraction summary — what the run READ and what it FOUND,
                             straight from the freshly-written register memory. -->
                        <template v-if="phase === 'done' && store">
                            <div class="mt-2 text-sm text-surface-700">
                                {{ ct('chat.action_result.summary_found', recordCount) }}
                            </div>
                            <div v-if="sourceFilenames.length" class="mt-1.5">
                                <div class="text-xs font-medium text-surface-500 mb-1">
                                    {{ ct('chat.action_result.summary_read', sourceFilenames.length) }}
                                </div>
                                <!-- A broad run can read 100+ files: past a handful the chip wall
                                     gets a loose filter (case/accent/word-order-insensitive) and a
                                     scroll box instead of swallowing the card. -->
                                <InputText
                                    v-if="manyFiles"
                                    v-model="filesFilter"
                                    class="w-full mb-1"
                                    size="small"
                                    :placeholder="ct('chat.register_files.filter_placeholder')"
                                />
                                <div
                                    class="flex flex-wrap gap-1 content-start"
                                    :class="manyFiles ? 'max-h-40 overflow-y-auto pr-1' : ''"
                                >
                                    <template v-for="(f, i) in filteredFilenames" :key="i">
                                        <!-- A file that resolved opens (MYAC-1642); the rest stay names. -->
                                        <DocRef
                                            v-if="sourceHandles.get(f)"
                                            :name="f"
                                            :handle="sourceHandles.get(f)"
                                            class="max-w-full"
                                        />
                                        <span
                                            v-else
                                            class="inline-flex items-start gap-1 text-xs bg-surface-100 rounded px-2 py-0.5 text-surface-600 max-w-full"
                                        >
                                            <i class="pi pi-file text-surface-400 mt-0.5 flex-shrink-0" />
                                            <span class="break-words min-w-0">{{ f }}</span>
                                        </span>
                                    </template>
                                </div>
                                <p v-if="filesFilter && !filteredFilenames.length" class="text-xs text-surface-500 py-1">
                                    {{ ct('chat.register_files.filter_no_match') }}
                                </p>
                            </div>
                            <template v-if="runSummary">
                                <button
                                    type="button"
                                    class="mt-2 inline-flex items-center gap-1 text-xs text-surface-500 hover:text-surface-700"
                                    @click="summaryExpanded = !summaryExpanded"
                                >
                                    <i :class="summaryExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" class="text-[10px]" />
                                    {{ ct('chat.action_result.summary_details') }}
                                </button>
                                <div
                                    v-if="summaryExpanded"
                                    class="mt-1 rounded-md border border-surface-200 bg-surface-50 p-2"
                                >
                                    <AiGeneratedTag class="mb-1" />
                                    <Markdown
                                        :content="runSummary"
                                        density="compact"
                                        class="text-xs leading-relaxed text-surface-600"
                                    />
                                </div>
                            </template>
                        </template>
                    </div>
                </div>
            </template>
        </Card>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import Markdown from '@/components/shared/Markdown.vue'
import DocRef from '@/components/referentials/DocRef.vue'
import type { DocumentHandle } from '@/models/DocumentHandle'
import AiGeneratedTag from '@/components/shared/AiGeneratedTag.vue'
import { useChatScope } from '@/composables/useChatScope'
import { useCardLocale } from '@/composables/useCardLocale'
import { useQMSCapaListStore } from '@/stores/qmsCapaList'
import { useQMSNcListStore } from '@/stores/qmsNcList'
import { useQMSChangeControlListStore } from '@/stores/qmsChangeControlList'
import { useQMSSupplierListStore } from '@/stores/qmsSupplierList'
import { useChatbotStore, ACTION_FOLLOWUP_PREFIX, type ActionResultCard } from '@/stores/chatbot'
import { matchesLoose } from '@/utils/looseMatch'

const props = defineProps<{ card: ActionResultCard }>()

// The project the conversation is about, on a project page and on the dashboard alike.
const { scopeProjectId } = useChatScope()
const chatbotStore = useChatbotStore()
const { ct } = useCardLocale(computed(() => chatbotStore.conversationLanguage ?? undefined))
const capaStore = useQMSCapaListStore()
const ncStore = useQMSNcListStore()
const changeControlStore = useQMSChangeControlListStore()
const supplierStore = useQMSSupplierListStore()

// Every register is backed by a store watching its memory + trigger assessment —
// supplier included since the extraction-summary work (it used to sit on a static
// "started" line with no live tracking).
const store = computed(() => {
    if (props.card.register === 'capa') return capaStore
    if (props.card.register === 'nc') return ncStore
    if (props.card.register === 'change_control') return changeControlStore
    if (props.card.register === 'supplier') return supplierStore
    return null
})

const registerLabel = computed(() => ct(`chat.action_approval.register.${props.card.register}`))

onMounted(() => {
    const pid = scopeProjectId.value
    if (pid && store.value) store.value.subscribe(pid)
})

// MYAC-954: read from the registers read-model, not the Assessment doc. `lastRunAtMs` is
// stamped only at a terminal, so the "is this MY run" test below is unchanged.
const triggerStatus = computed(() => store.value?.lastRunStatus ?? null)
const triggerUpdatedAt = computed(() => store.value?.lastRunAtMs ?? null)

// A 'done'/'failed' status reflects THIS run only once the run finished at/after
// the card was created. This avoids both flashing a stale previous-run result before our run
// starts, AND getting stuck on "refreshing" when the run finished before the card mounted.
const isThisRunFinished = computed(() =>
    triggerUpdatedAt.value != null && triggerUpdatedAt.value >= props.card.startedAt)

type Phase = 'submitted' | 'running' | 'done' | 'failed'
const phase = computed<Phase>(() => {
    if (!store.value) return 'submitted' // unknown register — no live tracking
    const s = triggerStatus.value
    if (s === 'failed' && isThisRunFinished.value) return 'failed'
    if (s === 'done' && isThisRunFinished.value) return 'done'
    return 'running'
})

// What the finished run produced — read from the register store, which watches the
// memory the run just wrote, so this updates the moment the fresh table lands.
const recordCount = computed(() => store.value?.recordCount ?? 0)
const sourceFilenames = computed(() =>
    (store.value?.sourceDocuments ?? [])
        .map((d) => d.filename ?? '')
        .filter(Boolean))
const sourceHandles = computed(() => {
    const out = new Map<string, DocumentHandle>()
    for (const d of store.value?.sourceDocuments ?? []) {
        if (d.filename && d.document_handle) out.set(d.filename, d.document_handle)
    }
    return out
})
const runSummary = computed(() => store.value?.runSummary ?? null)
const summaryExpanded = ref(false)

// Past a handful of files the chip wall needs a filter + scroll box.
const filesFilter = ref('')
const manyFiles = computed(() => sourceFilenames.value.length > 10)
const filteredFilenames = computed(() =>
    filesFilter.value
        ? sourceFilenames.value.filter((f) => matchesLoose(f, filesFilter.value))
        : sourceFilenames.value)

// MYAC-796 step 2 — auto-continue: once the run is done, if the user attached a follow-up
// ("…and do a bilan"), send it as a turn so the agent answers it with the fresh data. Fires
// exactly once (local guard + the consumed flag persisted on the assessment), and waits until
// the chat is idle so it doesn't collide with an in-flight turn.
let followupFired = false
watch(
    () => phase.value === 'done'
        && !!store.value?.chatFollowup
        && !store.value?.chatFollowupConsumed
        && !chatbotStore.isStreaming
        && !chatbotStore.isLoading,
    (ready) => {
        if (!ready || followupFired) return
        const followup = store.value?.chatFollowup
        if (!followup) return
        followupFired = true
        store.value?.markFollowupConsumed()
        // Prefix so it's hidden from the transcript; the chatbot strips it before the agent.
        chatbotStore.sendMessage(`${ACTION_FOLLOWUP_PREFIX} ${followup}`)
    },
    { immediate: true },
)

const headline = computed(() => {
    switch (phase.value) {
        case 'failed':
            return ct('chat.action_result.failed', { register: registerLabel.value })
        case 'done':
            // Reactive flow: the run wrote the register directly; the done line
            // invites the user to double-check and flag missing/extra files.
            return ct('chat.action_result.done', { register: registerLabel.value })
        case 'submitted':
            return ct('chat.action_result.supplier_started')
        default:
            return ct('chat.action_result.running', { register: registerLabel.value })
    }
})

const frameClass = computed(() => {
    switch (phase.value) {
        case 'done': return 'border-green-200 bg-green-50'
        case 'failed': return 'border-red-200 bg-red-50'
        default: return 'border-surface-200 bg-surface-50'
    }
})
const iconClass = computed(() => {
    switch (phase.value) {
        case 'done': return 'pi pi-check-circle text-green-600 mt-0.5'
        case 'failed': return 'pi pi-times-circle text-red-600 mt-0.5'
        default: return 'pi pi-spin pi-spinner text-surface-500 mt-0.5'
    }
})
</script>
